import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import { useUpdateResource } from '@app/src/api/updateHooks'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import { NotificationSeverity } from '@app/src/wf-constants'
import { useQueryClient } from '@tanstack/react-query'
import { useIntl } from 'react-intl'

export interface RiskSettingUpdate {
  riskIndexId: number
  isActivated: boolean
}

/**
 * Batch-saves risk index activation changes and refreshes the affected caches.
 * Shared by the legacy risk-screening table and the new topic cards.
 */
export const useSaveRiskSettings = () => {
  const { formatMessage } = useIntl()
  const { mutateAsync, isPending: isSaving } = useUpdateResource<RiskSettingUpdate[]>()
  const { showSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const saveRiskSettings = async (updates: RiskSettingUpdate[]): Promise<boolean> => {
    if (updates.length === 0) return true

    try {
      await mutateAsync({ url: endpoints.organizationRiskSettings, body: updates })

      showSnackbar({
        message: formatMessage({ id: 'general.riskSettingsSaved' }),
        severity: NotificationSeverity.success,
      })
      queryClient.invalidateQueries({ queryKey: [FetchKey.OrganizationRiskSettings] })
      queryClient.invalidateQueries({ queryKey: [FetchKey.AssessmentTemplateRiskIndicesCollection] })
      queryClient.removeQueries({ queryKey: [FetchKey.FilterFacet, 'riskStatus'] })
      queryClient.removeQueries({ queryKey: [FetchKey.RiskScreening] })

      return true
    } catch {
      showSnackbar({
        message: formatMessage({ id: 'notifications.toggleRiskIndexFailed' }),
        severity: NotificationSeverity.error,
      })
      return false
    }
  }

  return { saveRiskSettings, isSaving }
}

export default useSaveRiskSettings
